import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useCookies } from "react-cookie";
import { Controller, FieldErrors, FieldPath, useForm } from "react-hook-form";
import classNames from "classnames";
import { EmailInputFL, InputFL, SelectInputFL, ValidationSummary } from "Components/Inputs";
import { ModalBody, SpinningButton } from "Components/ReactstrapSlim";
import { CompanySize, EnterpriseClient, EnterpriseDemoRequest, IEnterpriseDemoRequest, InterestReason, SolutionType } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import { trackLeadCaptureForm } from "Utils/gaTrackers";
import { userSelector } from "Utils/selectors";

import classes from "./styles.module.scss"
import { ContactUsModalProps } from "../ContactUsModal";
import { yupResolver } from "@hookform/resolvers/yup";
import { Shape } from "Validation/yup/Shape";
import * as yup from "yup";
import { FormType } from "../FormType";
import CloseButton from "../CloseButton";
import ReCAPTCHA, { ReCAPTCHARef } from "Components/Recaptcha/ReCAPTCHA";

export interface ContactUsForm extends Pick<IEnterpriseDemoRequest, "email" | "fullName" | "phoneNumber" | "company" | "companySize" | "solution" | "question" | "recaptchaToken" | "interestReason"> {
   formType: FormType;
}

export const ContactUsSchema = yup.object<Shape<ContactUsForm>>({
   formType: yup.mixed<FormType>().defined(),
   fullName: yup.string().fullName(true, "Full name is required").defined(),
   email: yup.string()
      .required("Email is required")
      .email("Email is not valid")
      .defined(),
   phoneNumber: yup.string().phone("Phone number is not valid"),
   company: yup.string().when("formType", {
      is: FormType.Specific,
      then: yup.string().trim().required("Company name is required").defined(),
      otherwise: undefined
   }),
   companySize: yup.mixed<CompanySize>().when("formType", {
      is: FormType.Specific,
      then: yup.mixed<CompanySize>().required("Company size is required"),
      otherwise: undefined
   }),
   interestReason: yup.mixed<InterestReason>().when("formType", {
      is: FormType.Specific,
      then: yup.mixed<InterestReason>().required("Field is required"),
      otherwise: undefined
   }),
   solution: yup.mixed<SolutionType>().when("formType", {
      is: FormType.General,
      then: yup.mixed<SolutionType>().optional(),
      otherwise: undefined
   }),
   question: yup.string().optional(),
   recaptchaToken: yup.string().required("Please confirm you are not a robot").defined()
});


interface ModalBodyFormProps extends ContactUsModalProps {
   setShowSuccessScreen(value: InterestReason | undefined): void;
}

export interface ModalBodyFormRef {
   setFocus(path: FieldPath<ContactUsForm>): void;
}

const ModalBodyForm = React.forwardRef<ModalBodyFormRef, ModalBodyFormProps>((props, ref) => {
   const {
      setShowSuccessScreen,
      selectedPlan,
      email,
      formType,
      toggle,
      mode,
      formName,
      defaultValues,
      interestReason
   } = props;

   const enteripseClient = useApiClient(EnterpriseClient);
   const user = useSelector(userSelector);
   const [serverError, setServerError] = useState<Array<[string, string]>>([]);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
   const [cookies] = useCookies(["hubspotutk"]);


   const formMethods = useForm<ContactUsForm>({
      mode: "onTouched",
      resolver: yupResolver(ContactUsSchema),
      defaultValues: {
         ...defaultValues,
         formType: formType,
         email: email || user?.email || undefined,
         fullName: user?.fullName ?? defaultValues?.fullName ?? undefined,
         interestReason: interestReason ? InterestReason[interestReason] : undefined
      }
   });

   useImperativeHandle(ref, () => ({
      setFocus(path) {
         formMethods.setFocus(path);
      }
   }), [formMethods]);

   const formValues = formMethods.watch();

   const recaptchaRef = useRef<ReCAPTCHARef>();

   const submit = async () => {
      setIsSubmitting(true);
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const requestData = formMethods.getValues();
            const { formType, ...request } = requestData;

            const demoRequest = EnterpriseDemoRequest.fromJS({
               mode: mode,
               ...request,
               plan: selectedPlan,
               hubspotUTK: cookies.hubspotutk,
               referrerQuery: window.location.search
            });

            const requestResult = await enteripseClient.requestDemo(demoRequest);

            if (requestResult.success) {
               if (formName) {
                  LSHelper.ga.trackEnterpriseFormSubmission(formName);
               }
               setShowSuccessScreen(demoRequest.interestReason ?? InterestReason.Other);
            } else {
               setServerError([["requestDemo", requestResult.errorMessage || "Unexpected server error"]]);
            }

            trackLeadCaptureForm(mode, "submit");
         }
         else {
            let name: keyof FieldErrors<ContactUsForm>;
            for (name in formMethods.formState.errors) {
               formMethods.setFocus(name);
               break;
            }
         }
      } catch (err: any) {
         if (err.error) {
            setServerError([["requestDemo", err.error]]);
         }
         console.log(err);
      } finally {
         recaptchaRef.current?.reset();
         setIsSubmitting(false);
      }
   }

   useEffect(() => {
      ContactUsSchema.isValid(formValues).then(isValid => setSubmitButtonDisabled(!isValid));
   }, [formValues.company, formValues.companySize, formValues.interestReason, formValues.email, formValues.fullName, formValues.phoneNumber, formValues.solution, formValues.question, formValues.formType, formValues.recaptchaToken]);


   return <ModalBody aria-label="contact us" className={classes.modalBody}>
      <CloseButton toggle={toggle} />
      <div className={classNames(classes.heroSection)}>
         <div className={classes.heroSectionCaption}>Let’s connect–</div>
         <div className={classes.heroSectionDescription}>in just a few clicks.</div>
      </div>
      <div className={classNames(classes.heroSectionMobile)}>
         Let’s connect–in just a few clicks.
      </div>
      <div className={classes.formSection}>
         <div>
            <div className={classes.form}>
               <InputFL control={formMethods.control} name="formType" className="d-none" hidden />

               <InputFL control={formMethods.control} name="fullName" placeholder="Full Name" aria-label="full name" bsSize="sm" className={classNames(classes.firstInput)} readOnly={!!user?.email} />
               <EmailInputFL control={formMethods.control} name="email" placeholder="Email Address" aria-label="email address" bsSize="sm" autoComplete="off" readOnly={!!user?.fullName} />

               {
                  formType == FormType.Specific
                     ? <>
                        <InputFL control={formMethods.control} name="company" placeholder="Company Name" aria-label="company" bsSize="sm" />
                        <SelectInputFL control={formMethods.control} name="companySize" placeholder="Company Size" mobilePlaceholder="Size" aria-label="company size" bsSize="sm">
                           <option value={undefined} aria-label="none" className="d-none"></option>
                           <option value={CompanySize.Small}>1-99 employees</option>
                           <option value={CompanySize.Medium}>100-499 employees</option>
                           <option value={CompanySize.Large}>500-4,999 employees</option>
                           <option value={CompanySize.ExtraLarge}>5,000+ employees</option>
                        </SelectInputFL>
                     </>
                     : <></>
               }

               <InputFL control={formMethods.control} name="phoneNumber" placeholder="Phone Number" aria-label="phone(optional)" bsSize="sm" />

               {
                  formType == FormType.Specific
                     ? <>
                        {!interestReason && <SelectInputFL control={formMethods.control} name="interestReason" placeholder="I want to..." aria-label="i want to" bsSize="sm">
                           <option value={undefined} aria-label="none" className="d-none"></option>
                           <option value={InterestReason.Book}>Book - Find workspace for myself or my team</option>
                           <option value={InterestReason.Demo}>Demo - Enterprise solutions for flexible space and hybrid work</option>
                           <option value={InterestReason.List}>List - Monetize my workspaces on LiquidSpace</option>
                           <option value={InterestReason.Other}>Other</option>
                        </SelectInputFL>}
                     </>
                     : <></>
               }

               {
                  formType == FormType.General
                     ? <SelectInputFL control={formMethods.control} name="solution" placeholder="Solution for..." aria-label="what solution are you interested in?" bsSize="sm">
                        <option value={undefined} aria-label="none" className="d-none"></option>
                        <option value={SolutionType.Individual}>Individual</option>
                        <option value={SolutionType.Team}>Team</option>
                        <option value={SolutionType.Business}>Business</option>
                        <option value={SolutionType.Enterprise}>Enterprise</option>
                        <option value={SolutionType.Host}>Host</option>
                     </SelectInputFL>
                     : <></>
               }

               <InputFL control={formMethods.control} name="question" placeholder="Notes (Optional)" aria-label="optional notes" bsSize="sm" type="textarea" className={classNames(classes.notesInput)} />
               <div className={classes.recaptchaWrapper}>
                  <Controller
                     control={formMethods.control}
                     name="recaptchaToken"
                     render={({ field, fieldState }) =>
                        <>
                           <ReCAPTCHA
                              ref={x => { recaptchaRef.current = x ?? undefined; }}
                              size="normal"
                              sitekey={LiquidSpaceConfig.RecaptchaKey!}
                              action="enterprise_demo_request"
                              onChange={token => field.onChange(token || undefined)}
                              onExpired={() => field.onChange("")}
                           />
                           <div className={classNames("field-validation-error", classes.recaptchaValidation)}>{fieldState.error?.message}</div>
                        </>
                     } />
               </div>
               <SpinningButton className={classes.cta} color="primary" onClick={submit} isLoading={isSubmitting} aria-disabled={submitButtonDisabled} size="md" fluid="sm">Get in touch</SpinningButton>
            </div>
            <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverError} />
         </div>
      </div>
   </ModalBody>
});

export default ModalBodyForm;